import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Table,
    Spin,
    Typography,
    message,
    Input,
    Select,
    DatePicker,
    Row,
    Col,
    Modal,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { Button } from "antd";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function EmpCompoff() {
    const [compoffs, setCompoffs] = useState([]);
    const [filteredCompoffs, setFilteredCompoffs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCompoff, setSelectedCompoff] = useState(null);
    const [newStatus, setNewStatus] = useState("");
    const [cancelReason, setCancelReason] = useState("");
    const { authData, backUrl } = useContext(AuthContext);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Bulk approval states
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isBulkApproveModalOpen, setIsBulkApproveModalOpen] = useState(false);
    const [bulkApprovalReason, setBulkApprovalReason] = useState("Approved");

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [filters, setFilters] = useState({
        name: "",
        emp_code: "",
        status: "",
        dateRange: [],
        organisation: "",
        department: "",
        designation: "",
    });

    const fetchCompoffs = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backUrl}/api/hr/compoffs/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            const data = res.data?.results || res.data;
            setCompoffs(data);
            setFilteredCompoffs(data);
        } catch (error) {
            console.error("Error fetching compoff data:", error);
            message.error("Failed to load compoff records");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompoffs();
        fetchOrganisation();
    }, []);

    // -------------------------------
    //    FETCH ORG, DEPT, DESIGNATION
    // -------------------------------
    const fetchOrganisation = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            const results = res.data?.results ?? res.data ?? [];
            setOrganisations(results);
        } catch (err) {
            console.error("Error fetching organisations:", err);
            toast.error("Failed to load organisations");
        }
    };

    useEffect(() => {
        const orgValue = filters.organisation;
        if (orgValue) {
            fetchDepartments(orgValue);
        } else {
            setDepartments([]);
            setDesignations([]);
            setFilters((prev) => ({ ...prev, department: "", designation: "" }));
        }
        // eslint-disable-next-line
    }, [filters.organisation]);

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            const results = res.data?.results ?? res.data ?? [];
            setDepartments(results);
        } catch (err) {
            console.error("Error fetching departments:", err);
            toast.error("Failed to load departments");
        }
    };

    useEffect(() => {
        const deptValue = filters.department;
        if (deptValue) {
            fetchDesignations(deptValue);
        } else {
            setDesignations([]);
            setFilters((prev) => ({ ...prev, designation: "" }));
        }
        // eslint-disable-next-line
    }, [filters.department]);

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            const results = res.data?.results ?? res.data ?? [];
            setDesignations(results);
        } catch (err) {
            console.error("Error fetching designations:", err);
            toast.error("Failed to load designations");
        }
    };

    // -------------------------------
    //          FILTER LOGIC
    // -------------------------------
    useEffect(() => {
        let result = [...compoffs];

        if (filters.name) {
            result = result.filter((item) =>
                item?.employee?.first_name
                    ?.toLowerCase()
                    .includes(filters.name.toLowerCase())
            );
        }

        if (filters.emp_code) {
            result = result.filter((item) =>
                item?.employee?.emp_code
                    ?.toLowerCase()
                    .includes(filters.emp_code.toLowerCase())
            );
        }

        if (filters.organisation) {
            result = result.filter(
                (item) => String(item.employee.organisation) === String(filters.organisation)
            );
        }

        if (filters.department) {
            result = result.filter(
                (item) => String(item.employee.department) === String(filters.department)
            );
        }

        if (filters.designation) {
            result = result.filter(
                (item) => String(item.employee.designation) === String(filters.designation)
            );
        }

        if (filters.status) {
            result = result.filter(
                (item) =>
                    item.status?.toLowerCase() === filters.status.toLowerCase()
            );
        }

        if (filters.dateRange?.length === 2) {
            const [start, end] = filters.dateRange;
            result = result.filter((item) => {
                const itemDate = dayjs(item.date);
                return (
                    itemDate.isAfter(dayjs(start).subtract(1, "day")) &&
                    itemDate.isBefore(dayjs(end).add(1, "day"))
                );
            });
        }

        setFilteredCompoffs(result);
        setCurrentPage(1); // Reset to first page when filters change
    }, [filters, compoffs]);

    // -------------------------------
    //    BULK APPROVAL FUNCTIONS
    // -------------------------------
    const handleBulkApproveClick = () => {
        if (selectedRowKeys.length === 0) {
            toast.warning("Please select at least one compoff to approve");
            return;
        }
        setIsBulkApproveModalOpen(true);
    };

    const handleBulkApproveConfirm = async () => {
        if (!bulkApprovalReason.trim()) {
            toast.error("Please provide an approval reason");
            return;
        }

        try {
            await axios.post(
                `${backUrl}/api/hr/compoffs/approve-work/?is_employee_view=true`,
                {
                    compoff_ids: selectedRowKeys,
                    rejection_reason: bulkApprovalReason,
                },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success(`Successfully approved ${selectedRowKeys.length} compoff(s)`);
            setSelectedRowKeys([]);
            setBulkApprovalReason("Approved");
            setIsBulkApproveModalOpen(false);
            fetchCompoffs();
        } catch (error) {
            console.error("Bulk approval error:", error);
            toast.error(
                error.response?.data?.message || "Failed to approve compoffs"
            );
        }
    };

    const handleBulkApproveCancel = () => {
        setIsBulkApproveModalOpen(false);
        setBulkApprovalReason("Approved");
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
        },
        getCheckboxProps: (record) => ({
            // Optional: disable selection for already approved/rejected compoffs
            // disabled: record.status === "Approved" || record.status === "Rejected",
        }),
    };

    // -------------------------------
    //    PAGINATION HANDLER
    // -------------------------------
    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    // -------------------------------
    //    MODAL ACTIONS
    // -------------------------------
    const handleEditClick = (record) => {
        setSelectedCompoff(record);
        setNewStatus(record.status);
        setCancelReason("");
        setIsModalOpen(true);
    };

    const downloadExcel = () => {
        const rows = filteredCompoffs.map((item) => {
            const emp = item?.employee ?? {};

            return {
                "Employee Name": `${emp.first_name ?? ""} ${emp.last_name ?? ""}`.trim(),

                "Employee Code": emp.emp_code ?? "-",

                Organisation:
                    emp.organisation_name ??
                    emp.organisation ??
                    "-",

                Department:
                    emp.department_name ??
                    emp.department ??
                    "-",

                Designation:
                    emp.designation_name ??
                    emp.designation ??
                    "-",

                "Date Worked": item.work_date
                    ? new Date(item.work_date).toLocaleDateString("en-IN")
                    : "-",

                "Expiry Date": item.expiry_date
                    ? new Date(item.expiry_date).toLocaleDateString("en-IN")
                    : "-",

                Reason: item.reason ?? "-",
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Off Day");

        XLSX.writeFile(workbook, "Employee_Off_day.xlsx");
    };

    const handleModalOk = async () => {
        if (newStatus === "Cancelled" && !cancelReason.trim()) {
            toast.error("Please provide a cancellation reason before saving.");
            return;
        }

        try {
            await axios.post(
                `${backUrl}/api/hr/compoffs/${selectedCompoff.id}/cancel/`,
                { cancellation_reason: cancelReason },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success("Compoff status updated");
            fetchCompoffs();
        } catch (error) {
            console.error(error);
            toast.error("Failed to update compoff status.");
        }

        setIsModalOpen(false);
        setSelectedCompoff(null);
    };

    const handleModalCancel = () => {
        setIsModalOpen(false);
        setSelectedCompoff(null);
        setCancelReason("");
    };

    const columns = [
        { title: "Employee Name", dataIndex: ["employee", "full_name"], key: "name", fixed: "left" },
        { title: "Employee Code", dataIndex: ["employee", "emp_code"], key: "code" },
        { title: "Organisation", dataIndex: ["employee", "organisation_name"], key: "org" },
        { title: "Department", dataIndex: ["employee", "department_name"], key: "dept" },
        { title: "Designation", dataIndex: ["employee", "designation_name"], key: "desig" },
        {
            title: "Date Worked",
            dataIndex: "work_date",
            key: "work_date",
            render: (date) => new Date(date).toLocaleDateString("en-IN"),
        },
        {
            title: "Expiry Date",
            dataIndex: "expiry_date",
            key: "expiry_date",
            render: (date) => new Date(date).toLocaleDateString("en-IN"),
        },
        { title: "Reason", dataIndex: "reason", key: "reason" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <span
                    style={{
                        color:
                            status === "Available"
                                ? "green"
                                : status === "Rejected"
                                    ? "red"
                                    : status === "Availed"
                                        ? "orange"
                                        : "blue",
                        fontWeight: "bold",
                    }}
                >
                    {status}
                </span>
            ),
        },
    ];

    return (
        <div style={{ padding: "20px" }}>
            <ToastContainer />
            <Title level={4} style={{ marginBottom: 16, textTransform: "uppercase", color: "#0e497b" }}>
                Employee Off Day Records
            </Title>

            {/* FILTERS */}
            <div style={{ background: "#fff", padding: "15px", borderRadius: "10px", marginBottom: "15px" }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder="Employee Name"
                            value={filters.name}
                            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                        />
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Input
                            placeholder="Employee Code"
                            value={filters.emp_code}
                            onChange={(e) => setFilters({ ...filters, emp_code: e.target.value })}
                        />
                    </Col>

                    {/* Organisation */}
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Organisation"
                            value={filters.organisation || undefined}
                            onChange={(v) => setFilters({ ...filters, organisation: v })}
                            allowClear
                            style={{ width: "100%" }}
                        >
                            {Organisations.map((org) => (
                                <Option key={org.id} value={org.id}>
                                    {org.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    {/* Department */}
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Department"
                            value={filters.department || undefined}
                            onChange={(v) => setFilters({ ...filters, department: v })}
                            allowClear
                            disabled={!filters.organisation}
                            style={{ width: "100%" }}
                        >
                            {departments.map((dept) => (
                                <Option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    {/* Designation */}
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Designation"
                            value={filters.designation || undefined}
                            onChange={(v) => setFilters({ ...filters, designation: v })}
                            allowClear
                            disabled={!filters.department}
                            style={{ width: "100%" }}
                        >
                            {designations.map((des) => (
                                <Option key={des.id} value={des.id}>
                                    {des.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    {/* Status */}
                    <Col xs={24} sm={12} md={6}>
                        <Select
                            placeholder="Status"
                            value={filters.status || undefined}
                            onChange={(value) => setFilters({ ...filters, status: value })}
                            allowClear
                            style={{ width: "100%" }}
                        >
                            <Option value="Availed">Availed</Option>
                            <Option value="Available">Available</Option>
                            <Option value="Expired">Expired</Option>
                        </Select>
                    </Col>

                    {/* Date Range */}
                    <Col xs={24} sm={24} md={6}>
                        <RangePicker
                            format="DD/MM/YYYY"
                            value={filters.dateRange}
                            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                            style={{ width: "100%" }}
                        />
                    </Col>
                </Row>
            </div>

            {/* ACTION BUTTONS */}
            <div style={{ textAlign: "right", marginBottom: 12, display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                {/* <Button
                    type="primary"
                    style={{ background: "#0e497b", borderColor: "#0e497b" }}
                    onClick={handleBulkApproveClick}
                    disabled={selectedRowKeys.length === 0}
                >
                    Bulk Approve ({selectedRowKeys.length})
                </Button> */}
                <Button
                    type="primary"
                    style={{ background: "#0e497b", borderColor: "#0e497b" }}
                    onClick={downloadExcel}
                >
                    Download Excel
                </Button>
            </div>

            {/* TABLE */}
            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <Table
                        rowSelection={rowSelection}
                        columns={columns}
                        dataSource={filteredCompoffs}
                        rowKey={(record) => record.id}
                        bordered
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: filteredCompoffs.length,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50", "100"],
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: "max-content" }}
                        components={{
                            header: {
                                cell: (props) => (
                                    <th
                                        {...props}
                                        style={{
                                            backgroundColor: "#0e497b",
                                            color: "white",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {props.children}
                                    </th>
                                ),
                            },
                        }}
                    />
                </div>
            )}

            {/* BULK APPROVAL MODAL */}
            <Modal
                title="Bulk Approve Compensatory Off"
                open={isBulkApproveModalOpen}
                onOk={handleBulkApproveConfirm}
                onCancel={handleBulkApproveCancel}
                okText="Approve"
                centered
                okButtonProps={{
                    style: { backgroundColor: "#0e497b", borderColor: "#0e497b", color: "#fff" },
                }}
            >
                <p>
                    You are about to approve <strong>{selectedRowKeys.length}</strong> compoff record(s).
                </p>
                <Input.TextArea
                    placeholder="Enter approval reason (e.g., Approved)"
                    value={bulkApprovalReason}
                    onChange={(e) => setBulkApprovalReason(e.target.value)}
                    rows={3}
                />
            </Modal>

            {/* CANCEL MODAL */}
            <Modal
                title="Edit Compoff Status"
                open={isModalOpen}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText="Save"
                centered
                okButtonProps={{
                    style: { backgroundColor: "#0e497b", borderColor: "#0e497b", color: "#fff" },
                }}
            >
                <p>
                    <strong>Employee:</strong> {selectedCompoff?.employee?.first_name} (
                    {selectedCompoff?.employee?.emp_code})
                </p>

                <Select
                    value={newStatus}
                    onChange={(value) => setNewStatus(value)}
                    style={{ width: "100%", marginBottom: "10px" }}
                >
                    <Option value="Cancelled">Cancel</Option>
                </Select>

                {newStatus === "Cancelled" && (
                    <Input.TextArea
                        placeholder="Enter cancel reason"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={3}
                    />
                )}
            </Modal>
        </div>
    );
}